import { type ControllerConstructor, Application } from "@hotwired/stimulus"

import airbrakeClient from "@/helpers/airbrake"
import { IS_PRODUCTION_OR_STAGING } from "@/helpers/environment"

declare global {
  interface Window {
    Stimulus: Application
  }
}

if (!window.Stimulus) {
  window.Stimulus = Application.start()
}

const application = window.Stimulus

// Stimulus catches any exceptions it throws and only logs them by default making them invisible to
// us. Instead of doing that report errors on production to Airbrake.
application.handleError = (error: unknown, message: string, detail: object = {}): void => {
  const identifier = (detail as { identifier?: string }).identifier

  if (IS_PRODUCTION_OR_STAGING) {
    const err =
      error instanceof Error
        ? error
        : new Error(typeof error === "string" ? error : `Non-Error thrown (${typeof error})`)

    void airbrakeClient.notify({ error: err, params: { stimulusMessage: message, identifier } })
  } else {
    console.error(message, error, identifier ? `(${identifier})` : "") // eslint-disable-line no-console
  }
}

// When a controller with the same identifier is registered more than once, Stimulus
// tears down the first and uses the next. This can lead to cycles of connect/disconnect/connect
// which can cause bugs with commonly duplicated registrations, e.g. loaders. Check for existing
// modules with the same identifier and skip re-registration.
const register = (identifier: string, constructor: ControllerConstructor): void => {
  const isAlreadyRegistered = application.router.modules.some((m) => m.identifier === identifier)

  if (isAlreadyRegistered) {
    return
  }

  application.register(identifier, constructor)
}

export { register }
